import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { usePageTracking } from "../hooks/useAnalytics";
import PandaLoader from "../components/PandaLoader";

const HERO_ROTATION_MS = 6500;
const FALLBACK_IMAGE = "/assets/pixley-logo.png";
const TEMPLATES_PER_PAGE = 4;

const PencilIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const UserIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const BookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const SparkleIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2l1.6 5.8L19.5 9.5l-5.9 1.7L12 17l-1.6-5.8L4.5 9.5l5.9-1.7L12 2z" />
  </svg>
);

const BoltIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
  </svg>
);

const ChevronRightIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/**
 * On-brand mini loading indicator for processing story cards.
 *
 * - Circular ring with a Pixley blue → cyan gradient stroke.
 * - When percent is known, the stroke fills proportionally and the center
 *   shows the number.
 * - When percent is unknown, the whole ring rotates as an indeterminate
 *   spinner and the center shows three pulsing brand-color paint drops.
 * - A small "sparkle" dot rides the leading edge of the progress arc.
 */
const PaintProgressRing = ({ percent = null }) => {
  const size = 64;
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const known = typeof percent === "number" && !Number.isNaN(percent);
  const clamped = known ? Math.max(0, Math.min(100, percent)) : 0;
  const offset = c - (clamped / 100) * c;

  // Sparkle position on the leading edge of the arc (90deg = top of circle)
  const angle = (clamped / 100) * 360 - 90;
  const rad = (angle * Math.PI) / 180;
  const sparkleX = size / 2 + r * Math.cos(rad);
  const sparkleY = size / 2 + r * Math.sin(rad);

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        filter: "drop-shadow(0 4px 12px rgba(0,101,248,0.35))",
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          transform: "rotate(-90deg)",
          animation: known ? "none" : "paint-ring-spin 1.4s linear infinite",
        }}
      >
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth={stroke}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#0065F8"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={known ? offset : c * 0.7}
          style={{ transition: known ? "stroke-dashoffset 500ms cubic-bezier(.4,1.4,.6,1)" : "none" }}
        />
      </svg>

      {/* Sparkle on the leading edge — only when we have determinate progress */}
      {known && clamped > 0 && clamped < 100 && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: sparkleX,
            top: sparkleY,
            width: 8,
            height: 8,
            marginLeft: -4,
            marginTop: -4,
            borderRadius: "50%",
            background: "#FFFFFF",
            boxShadow: "0 0 10px 2px rgba(255,255,255,0.85), 0 0 18px 4px rgba(0,101,248,0.6)",
            animation: "paint-ring-sparkle 1.1s ease-in-out infinite",
          }}
        />
      )}

      {/* Center label */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Fredoka', sans-serif",
          color: "#FFFFFF",
          fontWeight: 800,
        }}
      >
        {known ? (
          <span style={{ fontSize: 14, letterSpacing: "-0.01em" }}>{Math.round(clamped)}%</span>
        ) : (
          <span style={{ display: "inline-flex", gap: 3 }}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: "#0065F8",
                  animation: `paint-ring-dot 0.9s ease-in-out ${i * 0.15}s infinite`,
                }}
              />
            ))}
          </span>
        )}
      </div>

      <style>{`
        @keyframes paint-ring-spin {
          to { transform: rotate(270deg); }
        }
        @keyframes paint-ring-sparkle {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.75; }
        }
        @keyframes paint-ring-dot {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50% { transform: translateY(-3px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const FeatureChip = ({ icon: Icon, label }) => {
  return (
    <div
      className="inline-flex items-center gap-2 px-3.5 py-2"
      style={{
        fontFamily: "'Fredoka', sans-serif",
        background: '#FFFFFF',
        color: '#0B1A33',
        fontWeight: 600,
        fontSize: 13,
        border: '2px solid rgba(11,26,51,0.08)',
        borderRadius: 999,
        boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
      }}
    >
      <span
        className="inline-flex h-5 w-5 items-center justify-center flex-shrink-0"
        style={{ color: '#0065F8' }}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="leading-tight">{label}</span>
    </div>
  );
};

const CompactTemplateCard = ({ template, onSelect }) => {
  const imageUrl = template?.image_url || FALLBACK_IMAGE;
  return (
    <button
      type="button"
      onClick={() => onSelect?.(template)}
      className="group relative flex w-full flex-col overflow-hidden text-left transition-all duration-200 hover:-translate-y-1"
      style={{
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        background: '#FFFFFF',
        border: '2px solid rgba(11,26,51,0.08)',
        borderRadius: 20,
        boxShadow: '0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)',
      }}
    >
      <div className="relative h-[170px] w-full overflow-hidden sm:h-[190px]">
        <img
          src={imageUrl}
          alt={template?.title || template?.name || "Template"}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = FALLBACK_IMAGE;
          }}
        />
        <span
          className="absolute left-3 top-3 px-2.5 py-1"
          style={{
            fontFamily: "'Fredoka', sans-serif",
            background: '#FFFFFF',
            color: '#0065F8',
            fontWeight: 800,
            fontSize: 10,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            border: '2px solid rgba(0,101,248,0.22)',
            borderRadius: 999,
            boxShadow: '0px 2px 0px rgba(11,26,51,0.06)',
          }}
        >
          {template?.category || "Story"}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 p-3.5">
        <div className="min-w-0">
          <h3
            className="truncate text-sm sm:text-base"
            style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33', fontWeight: 700, letterSpacing: '-0.01em' }}
          >
            {template?.name || template?.title || "Untitled"}
          </h3>
          {template?.description && (
            <p
              className="mt-0.5 truncate text-xs"
              style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85' }}
            >
              {template.description}
            </p>
          )}
        </div>
        <div
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center transition-all"
          style={{
            background: '#FFFFFF',
            color: '#0065F8',
            border: '2px solid rgba(0,101,248,0.22)',
            borderRadius: '50%',
            boxShadow: '0px 2px 0px rgba(11,26,51,0.06)',
          }}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </div>
      </div>
    </button>
  );
};

const StoryCard = ({
  story,
  onSelect,
  isProcessing = false,
  statusPercentage = null,
}) => {
  const imageUrl = story?.image_url || "/assets/pixley-logo.png";

  return (
    <button
      type="button"
      onClick={() => onSelect?.(story)}
      className="group relative h-[210px] w-[72vw] flex-shrink-0 overflow-hidden transition-all duration-200 hover:-translate-y-1 sm:w-[46vw] md:h-[220px] md:w-auto"
      role="listitem"
      style={{
        background: '#FFFFFF',
        border: '2px solid rgba(11,26,51,0.08)',
        borderRadius: 20,
        boxShadow: '0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)',
      }}
    >
      <img
        src={imageUrl}
        alt={story?.title || "Story card"}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
        onError={(event) => {
          event.currentTarget.src = "/assets/pixley-logo.png";
        }}
      />

      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to top, rgba(11,26,51,0.78) 0%, rgba(11,26,51,0.25) 35%, transparent 65%)',
        }}
      />

      {isProcessing && (
        <div
          className="absolute inset-0 transition-opacity duration-300"
          style={{
            borderRadius: 20,
            background: 'rgba(11,26,51,0.55)',
          }}
        >
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <PaintProgressRing percent={typeof statusPercentage === "number" ? statusPercentage : null} />
            <p
              className="px-2 text-center text-xs"
              style={{ fontFamily: "'Fredoka', sans-serif", color: '#FFFFFF', fontWeight: 700, letterSpacing: '0.02em' }}
            >
              {statusPercentage ? "Generating..." : "Creating your story"}
            </p>
          </div>
        </div>
      )}

      <span
        className="absolute left-3 top-3 px-2.5 py-1"
        style={{
          fontFamily: "'Fredoka', sans-serif",
          background: '#FFFFFF',
          color: '#0065F8',
          fontWeight: 800,
          fontSize: 10,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          border: '2px solid rgba(0,101,248,0.22)',
          borderRadius: 999,
          boxShadow: '0px 2px 0px rgba(11,26,51,0.06)',
        }}
      >
        {story?.category || "Story"}
      </span>

      <div className="absolute inset-x-0 bottom-0 p-3">
        <h3
          className="line-clamp-2 sm:text-base"
          style={{
            fontFamily: "'Fredoka', sans-serif",
            color: '#FFFFFF',
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '-0.01em',
          }}
        >
          {story?.name || story?.title || "Untitled Story"}
        </h3>
      </div>
    </button>
  );
};

const StoryRow = ({ section, onTemplateSelect }) => {
  const stories = Array.isArray(section?.stories) ? section.stories.slice(0, 4) : [];

  return (
    <section className="space-y-3" aria-label={section?.title || "Stories section"}>
      <div className="flex items-center">
        <h3
          className="sm:text-xl"
          style={{
            fontFamily: "'Fredoka', sans-serif",
            color: '#0B1A33',
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: '-0.01em',
            textTransform: 'uppercase',
          }}
        >
          {section?.title || "Stories"}
        </h3>
      </div>

      <div className="md:hidden">
        <div className="flex gap-3 overflow-x-auto pb-2" role="list">
          {stories.map((story) => (
            <StoryCard key={story.id} story={story} onSelect={onTemplateSelect} />
          ))}
        </div>
      </div>

      <div className="hidden grid-cols-2 gap-4 md:grid lg:grid-cols-4" role="list">
        {stories.map((story) => (
          <StoryCard key={story.id} story={story} onSelect={onTemplateSelect} />
        ))}
      </div>
    </section>
  );
};

const DEFAULT_NUM_CHARACTERS = 2;
const INITIAL_CHARACTERS_SHOWN = 5;

const StoryTemplatePage = () => {
  usePageTracking("Story Template");
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { user, subscriptionStatus, updateUser } = useAuth();
  const { currentProfile } = useProfile();

  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [heroIndex, setHeroIndex] = useState(0);
  const [userStories, setUserStories] = useState([]);
  const [isStoriesLoading, setIsStoriesLoading] = useState(true);
  const [storiesError, setStoriesError] = useState("");
  const [templatePage, setTemplatePage] = useState(0);
  const [showAllTemplates, setShowAllTemplates] = useState(false);

  // Modal state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [isCharactersLoading, setIsCharactersLoading] = useState(false);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState([]);
  const [showAllCharacters, setShowAllCharacters] = useState(false);
  const [isCreatingStory, setIsCreatingStory] = useState(false);

  const fetchStoryTemplates = useCallback(async () => {
    if (!apiClient) return;

    setFetchError("");
    setIsLoading(true);

    try {
      const response = await apiClient.getStoryTemplates();
      const isSuccess =
        response?.status === true || response?.status === "success";

      if (!isSuccess) {
        throw new Error(
          response?.error ||
            response?.detail ||
            "We couldn't load story sections right now. Please try again."
        );
      }

      const categoriesObject = response?.data;
      if (
        !categoriesObject ||
        typeof categoriesObject !== "object" ||
        Array.isArray(categoriesObject)
      ) {
        throw new Error("Invalid API response format");
      }

      const normalizedSections = Object.entries(categoriesObject).map(
        ([categoryTitle, stories]) => ({
          title: categoryTitle,
          stories: Array.isArray(stories)
            ? stories.map((story) => ({
                ...story,
                category: story?.category || categoryTitle,
              }))
            : [],
        })
      );

      setSections(normalizedSections);
      setHeroIndex(0);
    } catch (error) {
      setFetchError(
        error?.message ||
          "We couldn't load story sections right now. Please try again."
      );
      setSections([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiClient]);

  const visibleSections = useMemo(() => sections.slice(0, 3), [sections]);
  const templateStories = useMemo(
    () =>
      (Array.isArray(userStories) ? userStories : []).filter(
        (story) => story?.template_id !== null && story?.template_id !== undefined
      ),
    [userStories]
  );
  const heroTemplates = useMemo(
    () =>
      sections.flatMap((section) =>
        (section.stories || []).map((story) => ({
          ...story,
          category: story?.category || section.title,
          categoryTitle: section.title,
        }))
      ),
    [sections]
  );
  const heroTemplate =
    heroTemplates.length > 0 ? heroTemplates[heroIndex % heroTemplates.length] : null;

  const pagerTemplates = useMemo(() => {
    const rows = sections.map((section) =>
      (section.stories || []).map((story) => ({
        ...story,
        category: story?.category || section.title,
        categoryTitle: section.title,
      }))
    );
    const maxLen = rows.reduce((max, row) => Math.max(max, row.length), 0);
    const interleaved = [];
    for (let i = 0; i < maxLen; i += 1) {
      for (const row of rows) {
        if (row[i]) interleaved.push(row[i]);
      }
    }
    return interleaved;
  }, [sections]);

  const totalPages = Math.max(1, Math.ceil(pagerTemplates.length / TEMPLATES_PER_PAGE));
  const currentPageTemplates = useMemo(() => {
    const start = templatePage * TEMPLATES_PER_PAGE;
    return pagerTemplates.slice(start, start + TEMPLATES_PER_PAGE);
  }, [pagerTemplates, templatePage]);

  useEffect(() => {
    if (templatePage >= totalPages) setTemplatePage(0);
  }, [templatePage, totalPages]);

  // Character loading for modal
  const loadCharacters = useCallback(async () => {
    if (!apiClient || !user?.id) return;
    setIsCharactersLoading(true);
    try {
      const res = await apiClient.getUserCharacters(user.id, currentProfile?.id);
      if (res?.status === true || res?.status === "success") {
        const normalized = (Array.isArray(res?.data) ? res.data : [])
          .map((c) => ({
            id: c?.id,
            name: c?.name || "Unnamed",
            avatar_url:
              c?.avatar_url || c?.animated_image_url || c?.handdrawn_image_url ||
              c?.image_url || c?.animated_image || c?.handdrawn_image || null,
          }))
          .filter((c) => c.id != null);
        setCharacters(normalized);
      }
    } catch (_) {
      setCharacters([]);
    } finally {
      setIsCharactersLoading(false);
    }
  }, [apiClient, user?.id, currentProfile?.id]);

  // Load characters once on mount
  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  const maxCharacters = selectedTemplate?.num_characters || DEFAULT_NUM_CHARACTERS;

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedTemplate) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [selectedTemplate]);

  // Auto-select first N characters when modal opens
  useEffect(() => {
    if (!selectedTemplate || characters.length === 0) return;
    const needed = selectedTemplate?.num_characters || DEFAULT_NUM_CHARACTERS;
    const autoSelected = characters.slice(0, needed).map((c) => String(c.id));
    setSelectedCharacterIds(autoSelected);
    setShowAllCharacters(false);
  }, [selectedTemplate, characters]);

  const toggleCharacterSelection = (characterId) => {
    const id = String(characterId);
    setSelectedCharacterIds((prev) => {
      if (prev.includes(id)) return prev.filter((v) => v !== id);
      if (prev.length >= maxCharacters) {
        toast.error(`This story needs exactly ${maxCharacters} character${maxCharacters === 1 ? "" : "s"}`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleOpenTemplate = useCallback((template) => {
    if (!template?.id) return;
    setSelectedTemplate(template);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedTemplate(null);
    setSelectedCharacterIds([]);
    setShowAllCharacters(false);
  }, []);

  // Cost comes from the template itself (30s=50, 60s=75; legacy 90s=100).
  // The backend charges by the template's duration — never hardcode here.
  const templateStarCost = (template) => template?.star_cost ?? 100;

  const handleCreateFromModal = async () => {
    if (!apiClient || !user?.id || !selectedTemplate?.id || selectedCharacterIds.length !== maxCharacters) return;

    // Check if user has enough stars
    const starCost = templateStarCost(selectedTemplate);
    const currentStars = user?.pixley_stars ?? 0;
    if (currentStars < starCost) {
      toast.error(`Not enough stars. You need ${starCost} stars but have ${currentStars}.`);
      return;
    }

    // No pre-deduction: the backend deducts stars inside /createStory.

    setIsCreatingStory(true);
    toast.success("Your story is being created!");
    handleCloseModal();
    navigate("/storyverse");

    const _apiClient = apiClient;
    const _templateId = selectedTemplate.id;
    const _userId = user.id;
    const _profileId = currentProfile?.id;
    const _charIds = [...selectedCharacterIds];

    (async () => {
      try {
        const formData = new FormData();
        formData.append("template_id", _templateId);
        formData.append("user_id", _userId);
        formData.append("target_duration", "90");
        if (_profileId) formData.append("profile_id", _profileId);
        _charIds.forEach((id) => formData.append("characters", id));

        const payload = await _apiClient.uploadFileToEndpoint("/createStory", formData);
        const ok = payload?.status === true || payload?.status === "success" || payload?.success === true;
        if (!ok) throw new Error(payload?.error || payload?.detail || "Failed to create story");
        const remaining = payload?.remaining_stars ?? payload?.data?.remaining_stars;
        if (remaining !== undefined) {
          updateUser({ ...user, pixley_stars: remaining });
        }
      } catch (error) {
        toast.error(error?.message || "Could not create story right now.");
      }
    })();
  };

  const selectedIdSet = new Set(selectedCharacterIds);
  const atSelectionLimit = selectedCharacterIds.length >= maxCharacters;
  const visibleCharacters = showAllCharacters ? characters : characters.slice(0, INITIAL_CHARACTERS_SHOWN);
  const isCreateDisabled = isCreatingStory || selectedCharacterIds.length !== maxCharacters;

  const loadUserStories = useCallback(async () => {
    if (!apiClient) return;

    setStoriesError("");
    setIsStoriesLoading(true);

    try {
      const storiesResponse = await apiClient.getUserStories();
      const isStoriesSuccess =
        storiesResponse?.status === true || storiesResponse?.status === "success";

      if (!isStoriesSuccess) {
        throw new Error(
          storiesResponse?.error ||
            storiesResponse?.detail ||
            "Failed to load your stories"
        );
      }

      setUserStories(Array.isArray(storiesResponse?.data) ? storiesResponse.data : []);
    } catch (error) {
      setStoriesError(
        error?.message || "We couldn't load your stories right now."
      );
      setUserStories([]);
    } finally {
      setIsStoriesLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    if (!apiClient) return;
    fetchStoryTemplates();
  }, [apiClient, fetchStoryTemplates]);

  useEffect(() => {
    if (!apiClient) return;
    loadUserStories();
  }, [apiClient, loadUserStories]);

  useEffect(() => {
    if (heroTemplates.length <= 1) return undefined;

    const intervalId = window.setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroTemplates.length);
    }, HERO_ROTATION_MS);

    return () => window.clearInterval(intervalId);
  }, [heroTemplates.length]);

  return (
    <div
      className="storyverse-container pb-16 pt-24 sm:pt-28"
      style={{
        background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
        color: '#0B1A33',
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      {/* Soft brand blobs in the page background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          style={{
            position: 'absolute', top: -160, left: -160, width: 460, height: 460,
            borderRadius: '50%', background: 'rgba(0,101,248,0.07)', filter: 'blur(30px)',
          }}
        />
        <div
          style={{
            position: 'absolute', bottom: -200, right: -200, width: 520, height: 520,
            borderRadius: '50%', background: 'rgba(0,202,255,0.08)', filter: 'blur(30px)',
          }}
        />
      </div>

      <div className="relative w-full px-4 sm:px-6 lg:px-10">
        {isLoading ? (
          <section
            className="relative overflow-hidden p-6 sm:p-10"
            style={{
              background: '#FFFFFF',
              border: '2px solid rgba(11,26,51,0.06)',
              borderRadius: 28,
              boxShadow: '0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)',
            }}
          >
            <div
              className="h-[380px] animate-pulse md:h-[420px]"
              style={{ background: 'rgba(11,26,51,0.06)', borderRadius: 20 }}
            />
          </section>
        ) : (
          <section
            role="button"
            tabIndex={0}
            onClick={() => navigate("/create-story")}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                navigate("/create-story");
              }
            }}
            className="group relative cursor-pointer overflow-hidden transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              background: '#FFFFFF',
              border: '2px solid rgba(11,26,51,0.06)',
              borderRadius: 28,
              boxShadow: '0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)',
            }}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[220px] overflow-hidden lg:inset-y-0 lg:h-auto">
              <AnimatePresence mode="wait">
                {heroTemplate && (
                  <motion.img
                    key={`${heroTemplate.id}-${heroIndex}`}
                    src={heroTemplate.image_url || FALLBACK_IMAGE}
                    alt=""
                    aria-hidden="true"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />
                )}
              </AnimatePresence>
              {/* Mobile: fade image bottom into white */}
              <div
                className="absolute inset-0 lg:hidden"
                style={{
                  background:
                    "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 65%, #FFFFFF 100%)",
                }}
              />
              {/* Desktop: fade image left into white over the text column */}
              <div
                className="absolute inset-0 hidden lg:block"
                style={{
                  background:
                    "linear-gradient(to right, #FFFFFF 0%, #FFFFFF 40%, rgba(255,255,255,0.92) 53%, rgba(255,255,255,0.55) 70%, rgba(255,255,255,0) 87%)",
                }}
              />
            </div>

            {/* Soft brand-accent halo behind the headline */}
            <div
              className="pointer-events-none absolute -top-32 left-1/4 h-[300px] w-[55%] -translate-x-1/2"
              style={{ borderRadius: '50%', background: 'rgba(0,101,248,0.10)', filter: 'blur(30px)' }}
            />
            <div
              className="pointer-events-none absolute -bottom-24 -right-16 h-[260px] w-[260px]"
              style={{ borderRadius: '50%', background: 'rgba(0,202,255,0.08)', filter: 'blur(30px)' }}
            />

            <div className="relative z-10 px-6 pb-8 pt-[230px] sm:px-10 sm:pb-10 sm:pt-[250px] lg:max-w-[55%] lg:py-12 lg:pt-12">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  background: '#FFFFFF',
                  color: '#0065F8',
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: '0.04em',
                  border: '2px solid rgba(0,101,248,0.22)',
                  borderRadius: 999,
                  boxShadow: '0px 2px 0px rgba(11,26,51,0.06)',
                }}
              >
                <SparkleIcon className="h-3.5 w-3.5" />
                <span>Best way to create</span>
              </div>

              <h1
                className="mt-5 sm:text-5xl md:text-6xl"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  color: '#0B1A33',
                  fontWeight: 700,
                  fontSize: 'clamp(2rem, 4.5vw, 3.6rem)',
                  lineHeight: 1.02,
                  letterSpacing: '-0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Create Your<br />
                <span style={{ color: '#0065F8' }}>Own Story</span>
              </h1>

              <p
                className="mt-4 max-w-md sm:text-base"
                style={{ fontFamily: "'Fredoka', sans-serif", color: '#334155', fontSize: 14 }}
              >
                Bring your ideas, characters, and drawings to life in a one-of-a-kind adventure.
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                <FeatureChip icon={PencilIcon} label="Write your own storylines" />
                <FeatureChip icon={UserIcon} label="Use your own characters" />
                <FeatureChip icon={BookIcon} label="Personalized for your child" />
              </div>

              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate("/create-story");
                }}
                className="mt-8 inline-flex items-center justify-center px-10 py-4 transition-all active:scale-[0.98]"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  background: '#0065F8',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: 17,
                  border: 'none',
                  borderRadius: 999,
                  boxShadow: '0px 5px 0px #073B8E, 0px 14px 32px rgba(0,101,248,0.22)',
                  cursor: 'pointer',
                }}
              >
                Start Creating
              </button>
            </div>
          </section>
        )}

        <section className="mt-10 space-y-5">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span
                className="inline-block mb-1"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  color: '#0065F8',
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                }}
              >
                Quick Start
              </span>
              <div className="flex items-center gap-2">
                <BoltIcon className="h-5 w-5" style={{ color: '#0065F8' }} />
                <h2
                  className="sm:text-2xl"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    color: '#0B1A33',
                    fontWeight: 700,
                    fontSize: 22,
                    letterSpacing: '-0.01em',
                    textTransform: 'uppercase',
                    margin: 0,
                  }}
                >
                  Need a quick idea?
                </h2>
              </div>
              <p
                className="mt-1 text-sm"
                style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85' }}
              >
                Choose any of our storyline templates, and personalize with your own characters!
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowAllTemplates((value) => !value)}
              className="inline-flex shrink-0 items-center gap-1 transition"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                color: '#0065F8',
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {showAllTemplates ? "Show fewer" : "View all templates"}
              <ChevronRightIcon
                className={`h-3.5 w-3.5 transition-transform ${showAllTemplates ? "rotate-90" : ""}`}
              />
            </button>
          </div>

          {!isLoading && fetchError && (
            <div
              className="px-6 py-5"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                background: '#FFFFFF',
                color: '#DC2626',
                border: '2px solid rgba(220,38,38,0.25)',
                borderRadius: 22,
                boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
              }}
            >
              <p>{fetchError}</p>
              <button
                type="button"
                onClick={fetchStoryTemplates}
                className="mt-4 px-4 py-2 transition"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  background: '#FFFFFF',
                  color: '#0065F8',
                  fontWeight: 700,
                  fontSize: 13,
                  border: '2px solid rgba(0,101,248,0.22)',
                  borderRadius: 999,
                  boxShadow: '0px 2px 0px rgba(11,26,51,0.06)',
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {!isLoading && !fetchError && heroTemplates.length === 0 && (
            <div
              className="px-8 py-14 text-center"
              style={{
                background: '#FFFFFF',
                border: '2px solid rgba(11,26,51,0.06)',
                borderRadius: 28,
                boxShadow: '0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)',
              }}
            >
              <p style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33', fontWeight: 700, fontSize: 18 }}>
                No story templates available yet.
              </p>
              <p className="mt-2" style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85', fontSize: 14 }}>
                Please check back soon for new adventures.
              </p>
            </div>
          )}

          {!isLoading && !fetchError && heroTemplates.length > 0 && (
            <AnimatePresence mode="wait" initial={false}>
              {showAllTemplates ? (
                <motion.div
                  key="all"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-8"
                >
                  {visibleSections.map((section, index) => (
                    <StoryRow
                      key={`${section?.title || "section"}-${index}`}
                      section={section}
                      onTemplateSelect={handleOpenTemplate}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="pager"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="md:hidden">
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {pagerTemplates.slice(0, 8).map((template) => (
                        <div
                          key={template.id}
                          className="w-[72vw] flex-shrink-0 sm:w-[46vw]"
                        >
                          <CompactTemplateCard
                            template={template}
                            onSelect={handleOpenTemplate}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="hidden md:block">
                    {(() => {
                      // Group templates into pages of TEMPLATES_PER_PAGE (4 cards each).
                      const pages = [];
                      for (let i = 0; i < pagerTemplates.length; i += TEMPLATES_PER_PAGE) {
                        pages.push(pagerTemplates.slice(i, i + TEMPLATES_PER_PAGE));
                      }
                      return (
                        <>
                          <div
                            ref={(el) => {
                              if (!el) return;
                              // expose scroller for the dot buttons
                              window.__quickStartScroller = el;
                            }}
                            onScroll={(e) => {
                              const el = e.currentTarget;
                              const idx = Math.round(el.scrollLeft / el.clientWidth);
                              if (idx !== templatePage) setTemplatePage(idx);
                            }}
                            className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth gap-4 pb-2"
                            style={{
                              scrollbarWidth: 'none',
                              msOverflowStyle: 'none',
                            }}
                          >
                            <style>{`.cs-no-scrollbar::-webkit-scrollbar{display:none}`}</style>
                            {pages.map((group, pageIdx) => (
                              <div
                                key={`qs-page-${pageIdx}`}
                                className="snap-start flex-shrink-0 grid grid-cols-2 gap-4 lg:grid-cols-4 cs-no-scrollbar"
                                style={{ width: '100%' }}
                              >
                                {group.map((template) => (
                                  <CompactTemplateCard
                                    key={template.id}
                                    template={template}
                                    onSelect={handleOpenTemplate}
                                  />
                                ))}
                              </div>
                            ))}
                          </div>

                          {pages.length > 1 && (
                            <div className="mt-6 flex items-center justify-center gap-2">
                              {pages.map((_, index) => (
                                <button
                                  key={`dot-${index}`}
                                  type="button"
                                  onClick={() => {
                                    setTemplatePage(index);
                                    const el = window.__quickStartScroller;
                                    if (el) {
                                      el.scrollTo({
                                        left: index * el.clientWidth,
                                        behavior: 'smooth',
                                      });
                                    }
                                  }}
                                  aria-label={`Go to page ${index + 1}`}
                                  className="h-2.5 rounded-full transition-all"
                                  style={
                                    index === templatePage
                                      ? { width: 28, background: '#0065F8', boxShadow: '0px 2px 0px #073B8E' }
                                      : { width: 10, background: 'rgba(11,26,51,0.15)' }
                                  }
                                />
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </section>

        <section className="mt-12 w-full space-y-4">
          <div className="flex items-center justify-between">
            <h2
              style={{
                fontFamily: "'Fredoka', sans-serif",
                color: '#0B1A33',
                fontWeight: 700,
                fontSize: 24,
                letterSpacing: '-0.01em',
                textTransform: 'uppercase',
              }}
            >
              Your Stories
            </h2>
          </div>

          {isStoriesLoading && (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {[...Array(4)].map((_, index) => (
                <div
                  key={`story-skeleton-${index}`}
                  className="h-44 w-40 flex-shrink-0 animate-pulse"
                  style={{
                    background: 'rgba(11,26,51,0.06)',
                    border: '2px solid rgba(11,26,51,0.06)',
                    borderRadius: 16,
                  }}
                />
              ))}
            </div>
          )}

          {!isStoriesLoading && storiesError && (
            <div
              className="p-4"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                background: '#FFFFFF',
                color: '#DC2626',
                border: '2px solid rgba(220,38,38,0.25)',
                borderRadius: 18,
                boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
              }}
            >
              <p>{storiesError}</p>
              <button
                type="button"
                onClick={loadUserStories}
                className="mt-3 px-4 py-2 transition"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  background: '#FFFFFF',
                  color: '#0065F8',
                  fontWeight: 700,
                  fontSize: 13,
                  border: '2px solid rgba(0,101,248,0.22)',
                  borderRadius: 999,
                  boxShadow: '0px 2px 0px rgba(11,26,51,0.06)',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {!isStoriesLoading && !storiesError && templateStories.length === 0 && (
            <div
              className="p-5"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                background: '#FFFFFF',
                color: '#5A6B85',
                fontSize: 14,
                border: '2px solid rgba(11,26,51,0.06)',
                borderRadius: 18,
                boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
              }}
            >
              No template stories yet. Your newly created template story will appear here soon.
            </div>
          )}

          {!isStoriesLoading && !storiesError && templateStories.length > 0 && (
            <>
              <div className="md:hidden">
                <div className="flex gap-3 overflow-x-auto pb-2" role="list">
                  {templateStories.map((story) => {
                    const status = String(story?.status || "").toUpperCase();
                    const hasThumbnail = Boolean(
                      story?.thumbnail_url || story?.cover_image_url || story?.image_url
                    );
                    const isFullyReady =
                      Boolean(story?.file_url) &&
                      ["COMPLETED", "READY", "GENERATED"].includes(status);
                    const isProcessing = !isFullyReady;

                    return (
                      <StoryCard
                        key={story?.id}
                        story={{
                          ...story,
                          image_url:
                            story?.thumbnail_url ||
                            story?.cover_image_url ||
                            story?.image_url ||
                            FALLBACK_IMAGE,
                        }}
                        onSelect={(selectedStory) =>
                          selectedStory?.id && navigate(`/stories/${selectedStory.id}`)
                        }
                        isProcessing={isProcessing}
                        statusPercentage={
                          isProcessing && hasThumbnail
                            ? story?.status_percentage
                            : null
                        }
                      />
                    );
                  })}
                </div>
              </div>

              <div className="hidden grid-cols-2 gap-4 md:grid lg:grid-cols-4" role="list">
                {templateStories.map((story) => {
                  const status = String(story?.status || "").toUpperCase();
                  const hasThumbnail = Boolean(
                    story?.thumbnail_url || story?.cover_image_url || story?.image_url
                  );
                  const isFullyReady =
                    Boolean(story?.file_url) &&
                    ["COMPLETED", "READY", "GENERATED"].includes(status);
                  const isProcessing = !isFullyReady;

                  return (
                    <StoryCard
                      key={story?.id}
                      story={{
                        ...story,
                        image_url:
                          story?.thumbnail_url ||
                          story?.cover_image_url ||
                          story?.image_url ||
                          FALLBACK_IMAGE,
                      }}
                      onSelect={(selectedStory) =>
                        selectedStory?.id && navigate(`/stories/${selectedStory.id}`)
                      }
                      isProcessing={isProcessing}
                      statusPercentage={
                        isProcessing && hasThumbnail
                          ? story?.status_percentage
                          : null
                      }
                    />
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Template Character Selection Modal */}
      <AnimatePresence>
        {selectedTemplate && (
          <motion.div
            key="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden px-4"
            style={{ background: 'rgba(11,26,51,0.42)', backdropFilter: 'blur(8px)', fontFamily: "'Inter', 'Fredoka', sans-serif" }}
            onClick={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}
          >
            <motion.div
              key="modal-content"
              initial={{ opacity: 0, y: -40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -30, scale: 0.97 }}
              transition={{ type: "spring", damping: 28, stiffness: 350 }}
              className="relative flex w-full max-w-4xl max-h-[90vh] flex-col overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
                border: '2px solid rgba(11,26,51,0.06)',
                borderRadius: 28,
                boxShadow: '0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)',
              }}
            >
              {/* Template Header Image */}
              <div className="relative h-60 w-full flex-shrink-0 overflow-hidden sm:h-80">
                <img
                  src={selectedTemplate.image_url || FALLBACK_IMAGE}
                  alt={selectedTemplate.title || "Template"}
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
                />
                {/* Fade image into the modal's cream bg */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(to top, #EAF3FF 0%, rgba(234,243,255,0.55) 45%, rgba(234,243,255,0.10) 80%, transparent 100%)',
                  }}
                />

                {/* Close button */}
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center transition"
                  style={{
                    background: '#FFFFFF',
                    border: '2px solid rgba(11,26,51,0.08)',
                    borderRadius: 14,
                    boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                    color: '#334155',
                    cursor: 'pointer',
                  }}
                  aria-label="Close"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Template info overlaid on image */}
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <motion.span
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="inline-block px-3 py-1.5"
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      background: '#FFFFFF',
                      color: '#0065F8',
                      fontWeight: 800,
                      fontSize: 11,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      border: '2px solid rgba(0,101,248,0.22)',
                      borderRadius: 999,
                      boxShadow: '0px 2px 0px rgba(11,26,51,0.06)',
                    }}
                  >
                    {selectedTemplate.category || "Template"}
                  </motion.span>
                  <motion.h2
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="mt-3 sm:text-4xl"
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      color: '#0B1A33',
                      fontWeight: 700,
                      fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.05,
                      margin: 0,
                    }}
                  >
                    {selectedTemplate.title || selectedTemplate.name || "Untitled"}
                  </motion.h2>
                  {selectedTemplate.description && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="mt-2 line-clamp-2"
                      style={{ fontFamily: "'Fredoka', sans-serif", color: '#334155', fontSize: 14, lineHeight: 1.5, margin: '8px 0 0' }}
                    >
                      {selectedTemplate.description}
                    </motion.p>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-8 pt-5">

                {/* Character Selection */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  layout={false}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3
                        style={{
                          fontFamily: "'Fredoka', sans-serif",
                          color: '#0B1A33',
                          fontWeight: 700,
                          fontSize: 20,
                          letterSpacing: '-0.01em',
                          margin: 0,
                        }}
                      >
                        Choose Characters
                      </h3>
                      <p style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85', fontSize: 14, margin: '4px 0 0' }}>
                        Select {maxCharacters} character{maxCharacters === 1 ? "" : "s"} for this story
                      </p>
                    </div>
                    <span
                      style={{
                        fontFamily: "'Fredoka', sans-serif",
                        background: atSelectionLimit ? '#0065F8' : 'rgba(0,101,248,0.10)',
                        color: atSelectionLimit ? '#FFFFFF' : '#0065F8',
                        fontWeight: 800,
                        fontSize: 13,
                        letterSpacing: '0.04em',
                        padding: '6px 14px',
                        borderRadius: 999,
                        boxShadow: atSelectionLimit ? '0px 2px 0px #073B8E' : 'none',
                      }}
                    >
                      {selectedCharacterIds.length}/{maxCharacters}
                    </span>
                  </div>

                  {isCharactersLoading ? (
                    <div className="grid grid-cols-5 gap-4">
                      {[...Array(INITIAL_CHARACTERS_SHOWN)].map((_, i) => (
                        <div
                          key={`skel-${i}`}
                          className="aspect-square animate-pulse"
                          style={{ background: 'rgba(11,26,51,0.06)', borderRadius: 16 }}
                        />
                      ))}
                    </div>
                  ) : characters.length === 0 ? (
                    <div
                      className="p-5 text-center"
                      style={{
                        background: '#FFFFFF',
                        border: '2px solid rgba(11,26,51,0.08)',
                        borderRadius: 18,
                        boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
                      }}
                    >
                      <p style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85', fontSize: 15, margin: 0 }}>
                        No characters yet.
                      </p>
                      <button
                        type="button"
                        onClick={() => { handleCloseModal(); navigate("/character-lab"); }}
                        className="mt-4 px-5 py-3 transition active:scale-[0.98]"
                        style={{
                          fontFamily: "'Fredoka', sans-serif",
                          background: '#0065F8',
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: 14,
                          border: 'none',
                          borderRadius: 999,
                          boxShadow: '0px 4px 0px #073B8E',
                          cursor: 'pointer',
                        }}
                      >
                        Create Character
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-5 gap-4">
                        {visibleCharacters.map((character, idx) => {
                          const cId = String(character.id);
                          const isSelected = selectedIdSet.has(cId);
                          const isLocked = !isSelected && atSelectionLimit;

                          return (
                            <motion.button
                              key={cId}
                              type="button"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.15, delay: 0.03 * idx }}
                              whileTap={isLocked ? undefined : { scale: 0.93 }}
                              onClick={() => toggleCharacterSelection(cId)}
                              className="relative flex flex-col items-center gap-2.5 p-2.5 transition-all"
                              style={{
                                background: '#FFFFFF',
                                border: isSelected
                                  ? '3px solid #0065F8'
                                  : '2px solid rgba(11,26,51,0.08)',
                                borderRadius: 18,
                                boxShadow: isSelected
                                  ? '0px 4px 0px #073B8E, 0px 10px 24px rgba(0,101,248,0.18)'
                                  : '0px 2px 0px rgba(11,26,51,0.04)',
                                opacity: isLocked ? 0.45 : 1,
                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                transform: isSelected ? 'translateY(-2px)' : 'none',
                              }}
                            >
                              <div
                                className="relative aspect-square w-full overflow-hidden"
                                style={{ borderRadius: 14, background: '#EAF3FF' }}
                              >
                                <img
                                  src={character.avatar_url || FALLBACK_IMAGE}
                                  alt={character.name}
                                  className="h-full w-full object-cover"
                                  onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
                                />
                                {isSelected && (
                                  <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute"
                                    style={{
                                      top: 6,
                                      right: 6,
                                      width: 26,
                                      height: 26,
                                      borderRadius: '50%',
                                      background: '#0065F8',
                                      boxShadow: '0px 2px 0px #073B8E',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <svg width="14" height="14" fill="none" stroke="#FFFFFF" strokeWidth="3" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  </motion.div>
                                )}
                              </div>
                              <span
                                className="w-full truncate text-center"
                                style={{
                                  fontFamily: "'Fredoka', sans-serif",
                                  color: '#0B1A33',
                                  fontWeight: 700,
                                  fontSize: 13,
                                }}
                              >
                                {character.name}
                              </span>
                            </motion.button>
                          );
                        })}
                      </div>

                      {characters.length > INITIAL_CHARACTERS_SHOWN && (
                        <button
                          type="button"
                          onClick={() => setShowAllCharacters((v) => !v)}
                          className="mt-5 flex w-full items-center justify-center gap-2 py-3 transition-all"
                          style={{
                            fontFamily: "'Fredoka', sans-serif",
                            background: '#FFFFFF',
                            color: '#0065F8',
                            fontWeight: 700,
                            fontSize: 14,
                            border: '2px solid rgba(0,101,248,0.18)',
                            borderRadius: 999,
                            boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
                            cursor: 'pointer',
                          }}
                        >
                          {showAllCharacters ? "Show less" : `Show all ${characters.length} characters`}
                          <ChevronRightIcon className={`h-3 w-3 transition-transform ${showAllCharacters ? "-rotate-90" : "rotate-90"}`} />
                        </button>
                      )}
                    </>
                  )}
                </motion.div>

                {/* Create Button */}
                <motion.button
                  type="button"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  onClick={handleCreateFromModal}
                  disabled={isCreateDisabled}
                  className="mt-8 w-full transition-all active:scale-[0.98]"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    background: isCreateDisabled ? 'rgba(0,101,248,0.30)' : '#0065F8',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: 17,
                    border: 'none',
                    borderRadius: 999,
                    boxShadow: isCreateDisabled
                      ? 'none'
                      : '0px 5px 0px #073B8E, 0px 14px 32px rgba(0,101,248,0.22)',
                    padding: '18px 24px',
                    cursor: isCreateDisabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isCreatingStory ? "Creating..." : "Create Story ✨"}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StoryTemplatePage;
